import api from '../../../shared/utils/api';

/**
 * Acervo do deal — os documentos que pertencem a UM negócio.
 *
 * BFF-only: tudo passa pelo contrato `/api/v1` (o axios compartilhado está
 * fixo em `baseURL: '/api/v1'`), nunca direto num serviço de backend. O BFF
 * proxeia para `janus-sales-api`
 * (`/api/v1/pipeline/:dealId/documentos[...]`).
 *
 * **O contrato é snake_case de ponta a ponta** — e é de propósito. O proxy
 * usa `q2cRequestRaw` justamente para NÃO camelCasear: o `toCamelCase`
 * implícito renomearia `nome_do_arquivo`, `extracao_status` e
 * `texto_extraido`, e a tela leria `undefined`. Se um dia alguém trocar o
 * proxy para a variante camel, os tipos abaixo é que ficam errados — não o
 * backend.
 *
 * Os bytes NUNCA passam pelo agente de IA: o upload é HTTP puro, e o que o
 * agente recebe é só o `id` e os metadados. Mesmo desenho do clipe do Eka.
 */

/** O que o documento É na negociação. Vocabulário FECHADO no backend. */
export type TipoDeDocumento =
  | 'proposta'
  | 'contrato'
  | 'ata'
  | 'planilha'
  | 'apresentacao'
  | 'outro';

/**
 * Estado da EXTRAÇÃO de texto — separado do documento em si.
 *
 * `nao_suportado` NÃO é `falhou`: um .zip não tem texto para extrair, e
 * dizer "falhou" mandaria alguém investigar um não-defeito.
 */
export type StatusDaExtracao =
  | 'pendente'
  | 'pronta'
  | 'falhou'
  | 'nao_suportado';

export interface ExtracaoDoDocumento {
  status: StatusDaExtracao;
  /** Frase em pt-BR do backend. Vazia quando extraiu. Não reescrever aqui. */
  motivo: string | null;
  /**
   * Quantos caracteres temos. `0` com status `pronta` é um documento SEM
   * texto (um PDF só de imagem) — e isso é diferente de não termos extraído.
   */
  caracteres: number;
}

export interface DocumentoDoDeal {
  id: string;
  deal_id: string;
  /** Rótulo editável. Nasce do nome do arquivo. */
  titulo: string;
  /** O nome ORIGINAL, preservado. Renomear não o apaga. */
  nome_do_arquivo: string;
  tipo: TipoDeDocumento;
  content_type: string | null;
  tamanho_bytes: number;
  uploaded_by_user_id: number | null;
  /** `agente` = clipe do chat · `tela` = esta aba. */
  origem: 'agente' | 'tela';
  descricao: string | null;
  extracao: ExtracaoDoDocumento;
  created_at: string | null;
}

export interface AcervoDoDeal {
  total: number;
  /**
   * Quantos documentos NÃO têm texto extraído. Existe para a tela (e o
   * agente) não afirmarem que revisaram o acervo quando metade dele é PDF
   * escaneado.
   */
  sem_texto_extraido: number;
  documentos: DocumentoDoDeal[];
}

export interface AtualizarDocumentoPayload {
  titulo?: string;
  tipo?: TipoDeDocumento;
  descricao?: string;
}

const base = (dealId: string) =>
  `/pipeline/${encodeURIComponent(dealId)}/documentos`;

export const dealDocumentsApi = {
  listar: async (dealId: string): Promise<AcervoDoDeal> => {
    const { data } = await api.get<AcervoDoDeal>(base(dealId));
    return data;
  },

  /**
   * Anexa um arquivo. Deixa o erro do upstream subir — o chamador traduz
   * (413 = maior que 50 MB, 422 = arquivo vazio). Engolir aqui produziria
   * um "não deu" genérico sobre uma causa que o backend já nomeou.
   */
  anexar: async (
    dealId: string,
    arquivo: File,
    extras?: { titulo?: string; tipo?: TipoDeDocumento; descricao?: string },
  ): Promise<DocumentoDoDeal> => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    if (extras?.titulo) form.append('titulo', extras.titulo);
    if (extras?.tipo) form.append('tipo', extras.tipo);
    if (extras?.descricao) form.append('descricao', extras.descricao);
    form.append('origem', 'tela');
    const { data } = await api.post<DocumentoDoDeal>(base(dealId), form);
    return data;
  },

  detalhar: async (
    dealId: string,
    documentoId: string,
    incluirTexto = false,
  ): Promise<DocumentoDoDeal & { texto_extraido?: string | null }> => {
    const { data } = await api.get(
      `${base(dealId)}/${encodeURIComponent(documentoId)}`,
      { params: { incluir_texto: incluirTexto } },
    );
    return data;
  },

  atualizar: async (
    dealId: string,
    documentoId: string,
    payload: AtualizarDocumentoPayload,
  ): Promise<DocumentoDoDeal> => {
    const { data } = await api.patch<DocumentoDoDeal>(
      `${base(dealId)}/${encodeURIComponent(documentoId)}`,
      payload,
    );
    return data;
  },

  apagar: async (dealId: string, documentoId: string): Promise<void> => {
    await api.delete(`${base(dealId)}/${encodeURIComponent(documentoId)}`);
  },

  /**
   * Baixa os bytes. O backend serve o arquivo (não uma URL assinada): o
   * bucket é privado e assinatura expira, virando link quebrado que parece
   * arquivo perdido.
   */
  baixar: async (dealId: string, documentoId: string): Promise<Blob> => {
    const { data } = await api.get(
      `${base(dealId)}/${encodeURIComponent(documentoId)}/arquivo`,
      { responseType: 'blob' },
    );
    return data as Blob;
  },
};
